import { rolePermissions, getRoleDisplayName, type UserRole } from '@product-radar/shared';
import type { RolePermissions } from '@product-radar/shared';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Check, X } from 'lucide-react';

type PermissionKey = keyof RolePermissions;

const ROLES: UserRole[] = ['admin', 'product_manager', 'contributor', 'viewer'];

interface MatrixRow {
  label: string;
  indent: 0 | 1 | 2;
  permission: PermissionKey | PermissionKey[];
}

function hasAccess(role: UserRole, permission: PermissionKey | PermissionKey[]): boolean {
  if (Array.isArray(permission)) {
    return permission.every((p) => rolePermissions[role]?.[p] ?? false);
  }
  return rolePermissions[role]?.[permission] ?? false;
}

const MATRIX_ROWS: MatrixRow[] = [
  { label: 'Dashboard', indent: 0, permission: 'canAccessDashboard' },
  { label: 'Feedback', indent: 0, permission: 'canAccessFeedback' },
  {
    label: 'Create/Edit/Delete',
    indent: 1,
    permission: ['canCreateFeedback', 'canUpdateFeedback', 'canDeleteFeedback'],
  },
  { label: 'Ideas', indent: 0, permission: 'canAccessIdeas' },
  {
    label: 'Create/Edit/Delete',
    indent: 1,
    permission: ['canCreateIdeas', 'canUpdateIdeas', 'canDeleteIdeas'],
  },
  { label: 'Product Definition', indent: 0, permission: 'canAccessProductDefinition' },
  { label: 'Manage product structure', indent: 1, permission: 'canManageProductStructure' },
  { label: 'Clients', indent: 0, permission: 'canAccessClients' },
  {
    label: 'Create/Edit/Delete',
    indent: 1,
    permission: ['canCreateClients', 'canUpdateClients', 'canDeleteClients'],
  },
  { label: 'Integrations', indent: 0, permission: 'canAccessIntegrations' },
  { label: 'Configure integrations', indent: 1, permission: 'canConfigureIntegrations' },
  { label: 'Settings', indent: 0, permission: 'canAccessSettings' },
  { label: 'General settings', indent: 1, permission: 'canAccessGeneralSettings' },
  { label: 'Billing', indent: 1, permission: 'canAccessBillingSettings' },
  { label: 'User management', indent: 1, permission: 'canAccessUserManagement' },
  { label: 'Edit users', indent: 2, permission: 'canManageUsers' },
  { label: 'Security & privacy', indent: 1, permission: 'canAccessSecuritySettings' },
  { label: 'Notifications', indent: 1, permission: 'canAccessNotifications' },
];

function TreePrefix({ indent }: { indent: number }) {
  if (indent === 0) return null;
  const prefix = indent === 1 ? '└ ' : '  └ ';
  return <span className="text-muted-foreground font-normal">{prefix}</span>;
}

export function RolePermissionsMatrix() {
  return (
    <Card>
      <CardHeader className="py-3">
        <CardTitle className="text-base">Role permissions reference</CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <Table className="table-fixed">
          <TableHeader>
            <TableRow className="border-b">
              <TableHead className="py-1 px-2 text-xs font-medium w-[40%]">Permission</TableHead>
              {ROLES.map((role) => (
                <TableHead key={role} className="text-center py-1 px-2 text-xs font-medium w-[15%]">
                  {getRoleDisplayName(role)}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {MATRIX_ROWS.map((row, idx) => (
              <TableRow key={idx}>
                <TableCell
                  className={
                    'py-0.5 px-2 text-xs ' +
                    (row.indent === 0
                      ? 'font-medium'
                      : row.indent === 1
                        ? 'text-muted-foreground pl-4'
                        : 'text-muted-foreground pl-6')
                  }
                >
                  <TreePrefix indent={row.indent} />
                  {row.label}
                </TableCell>
                {ROLES.map((role) => (
                  <TableCell key={role} className="text-center py-0.5 px-2">
                    {hasAccess(role, row.permission) ? (
                      <Check className="h-3 w-3 text-green-600 inline-block" />
                    ) : (
                      <X className="h-3 w-3 text-red-600 inline-block" />
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
